import type {
  Variant,
  VariantListDataType,
  VariantTypeOption,
} from '../variant_check/redux/types';
import type { VariantTranscript } from '../variant_workflow/redux';
import { apiSlice } from './apiSlice';

/**
 * RTK Query endpoints for the Variant Check (search / create) feature.
 *
 * Migrated from the legacy createAsyncThunk implementations in
 * `variant_check/redux/async_thunks.ts` following the Redux Essentials
 * tutorial:
 * - Part 7 — RTK Query Basics:
 *   https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics
 * - Part 8 — RTK Query Advanced Patterns:
 *   https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced-patterns
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface GetVariantsArgs {
  limit?: number;
  offset?: number;
  variant_id?: number;
  search?: string;
  sortField?: string;
  sortOrder?: 'ascend' | 'descend';
  interpretation_status_filter?: string[];
  clinical_interpretation_filter?: string[];
  creator_filter?: string[];
  created_date_filter?: string[];
  created_date_from?: string;
  created_date_to?: string;
}

export interface GetVariantsResult {
  list: VariantListDataType[];
  total: number;
}

export interface VariantFilterOptions {
  interpretation_status: string[];
  clinical_interpretation: string[];
  creator: string[];
  created_date: string[];
}

export interface GeneSymbolSuggestion {
  gene_symbol: string;
  id: string | number;
}

export interface CreateVariantByMetaDataResult {
  variant_id: number;
  created: boolean;
  message: string;
}

export interface CreateVariantTaskResult {
  task_id: string;
  message: string;
}

// ─── Injected Endpoints ──────────────────────────────────────────────────────

export const variantCheckApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // The query argument object is the cache key: RTK Query does a shallow
    // stable comparison of its fields and refetches when any of them change
    getVariants: builder.query<GetVariantsResult, GetVariantsArgs>({
      query: ({
        limit,
        offset,
        variant_id,
        search,
        sortField,
        sortOrder,
        interpretation_status_filter,
        clinical_interpretation_filter,
        creator_filter,
        created_date_filter,
        created_date_from,
        created_date_to,
      }) => ({
        url: '/variant_main_table_search',
        params: {
          ...(typeof limit === 'number' ? { limit } : {}),
          ...(typeof offset === 'number' ? { offset } : {}),
          ...(typeof variant_id === 'number' ? { variant_id } : {}),
          ...(search ? { search } : {}),
          ...(sortField ? { sortField } : {}),
          ...(sortOrder ? { sortOrder } : {}),
          ...(interpretation_status_filter?.length
            ? { interpretation_status_filter }
            : {}),
          ...(clinical_interpretation_filter?.length
            ? { clinical_interpretation_filter }
            : {}),
          ...(creator_filter?.length ? { creator_filter } : {}),
          ...(created_date_filter?.length ? { created_date_filter } : {}),
          ...(created_date_from ? { created_date_from } : {}),
          ...(created_date_to ? { created_date_to } : {}),
        },
      }),
      // Extract just the fields we need before caching
      transformResponse: (data: {
        results: VariantListDataType[];
        total_result_count: number;
      }) => ({
        list: data.results,
        total: data.total_result_count,
      }),
      providesTags: ['Variants'],
    }),

    getVariantFilterOptions: builder.query<VariantFilterOptions, void>({
      query: () => ({ url: '/variant_main_table_filter_options' }),
      providesTags: ['Variants'],
      keepUnusedDataFor: Infinity,
    }),

    // Static lookup data — fetched once and kept for the whole session,
    // same pattern as the globalDataApi endpoints.
    getVariantTypeOptions: builder.query<VariantTypeOption[], void>({
      query: () => ({ url: '/variantdata/variant-type-options' }),
      providesTags: [{ type: 'GlobalData', id: 'VariantTypeOptions' }],
      keepUnusedDataFor: Infinity,
    }),

    // Autocomplete lookup — triggered imperatively via the lazy query hook.
    getGeneSymbols: builder.query<GeneSymbolSuggestion[], string>({
      query: (name) => ({
        url: '/variantdata/autocomplete_gene_symbol',
        params: { q: name },
      }),
    }),

    getTranscriptsByGene: builder.query<VariantTranscript[], string>({
      query: (geneId) => ({ url: `/variantdata/transcripts/${geneId}` }),
    }),

    getVariantMetadata: builder.query<
      Variant,
      { transcript_primaryid: string; cdna: string }
    >({
      // URL-encode cdna to handle special characters like > in cDNA notation
      // (e.g., c.6612C>A)
      query: ({ transcript_primaryid, cdna }) => ({
        url: `/variantdata/metadata/${transcript_primaryid}/${encodeURIComponent(cdna)}`,
      }),
      transformResponse: (data: { metadata: Variant }) => data.metadata,
    }),

    createVariantByMetaData: builder.mutation<
      CreateVariantByMetaDataResult,
      { transcript_primaryid: string; cdna: string; variant_type: string }
    >({
      query: ({ transcript_primaryid, cdna, variant_type }) => ({
        url: `/variantdata/variant/createbymetadata/${transcript_primaryid}/${encodeURIComponent(cdna)}`,
        method: 'POST',
        data: { type: variant_type },
      }),
      // Refetch the variants list after creating a new variant
      invalidatesTags: ['Variants'],
    }),

    createVariantInBackground: builder.mutation<
      CreateVariantTaskResult,
      { transcript_primaryid: string; cdna: string }
    >({
      query: ({ transcript_primaryid, cdna }) => ({
        url: `/variantdata/variant/create-async/${transcript_primaryid}/${encodeURIComponent(cdna)}`,
        method: 'POST',
      }),
      invalidatesTags: ['RunningTasks', 'Variants'],
    }),
  }),
});

// ─── Auto-generated Hooks ────────────────────────────────────────────────────

export const {
  useGetVariantsQuery,
  useGetVariantFilterOptionsQuery,
  useGetVariantTypeOptionsQuery,
  useLazyGetGeneSymbolsQuery,
  useLazyGetTranscriptsByGeneQuery,
  useLazyGetVariantMetadataQuery,
  useCreateVariantByMetaDataMutation,
  useCreateVariantInBackgroundMutation,
} = variantCheckApi;
